import React, { useContext, useEffect, useState } from 'react';
import { NecttosContext } from '../context/NecttosContext';
import { getAxiosTokenInstance } from '../utils/axiosInstance';
import { useHistory, useLocation } from 'react-router-dom';
import { NecttosLogo, OLoading } from '../static/svg/icons';
import Cookies from 'js-cookie';

export const CACHE_EXPIRY = 60 * 60 * 1000;
const authRoutes = ['/app', '/college-list'];

const checkAuth = async () => {
	try {
		const instance = await getAxiosTokenInstance();
		const { data } = await instance.get('/lanch/authing');
		return data;
	} catch (error) {
		throw error;
	}
};

function Auth({ children }) {
	const { collegeId, userId, setUserId, setCollegeList, setProfile, setIcons, setCollegeId, setRole, setSelectedCollege } = useContext(NecttosContext);
	const { setCollegeId: oldSetUserId } = useContext(NecttosContext);

	const [loadingMessage, setLoadingMessage] = useState('Authenticating...');
	const [authComplete, setAuthComplete] = useState(false);
	const [multipleInstituitions, setMultipleInstituitions] = useState(false);
	const [needData, setNeedData] = useState([]);
	const history = useHistory();
	const location = useLocation();
	const isTokenAvailable = Cookies.get('userToken');

	const getLocalAuthData = () => {
		try {
			const storedData = localStorage.getItem('authing');
			if (!storedData) return null;
			const { data, timestamp } = JSON.parse(storedData);
			const currentTime = Date.now();
			if (currentTime - timestamp > CACHE_EXPIRY) {
				localStorage.removeItem('authing');
				return null;
			}
			return data;
		} catch (error) {
			console.error('Error fetching local auth data:', error);
			return null;
		}
	};

	const storeAuthData = (data) => {
		try {
			const timestamp = Date.now();
			localStorage.setItem('authing', JSON.stringify({ data, timestamp }));
		} catch (error) {
			console.error('Error storing auth data:', error);
		}
	};

	const getAuthDetails = async () => {
		setLoadingMessage('Checking local storage...');
		const localAuthData = await getLocalAuthData();

		if (localAuthData?.profile?._id) {
			setLoadingMessage('Using cached data...');
			const { college, profile, multipleInstituitions, colleges, role, icons, needStatData } = localAuthData;

			if (localStorage.getItem('collegeId') === '' || localStorage.getItem('collegeId') == 'undefined' || localStorage.getItem('collegeId') == '""') {
				await setCollegeId(college?._id);
				await oldSetUserId(college?._id);
			}

			if (collegeId === college?._id) {
				await setRole(role);
				await setIcons(icons);
			} else {
				const matchedCollege = colleges.find((c) => c._id === collegeId);
				await setRole(matchedCollege?.roles);
				await setIcons(matchedCollege?.icons);
			}

			await setSelectedCollege({ ...college, collegeId: college._id, ...colleges.find((c) => c._id === college?._id) });
			await setProfile(profile);
			await setCollegeList(colleges || [college]);
			await setUserId(profile?._id);

			setNeedData(needStatData || []);
			setMultipleInstituitions(multipleInstituitions ?? false);
			setAuthComplete(true);
			setLoadingMessage('');
			return;
		}

		try {
			setLoadingMessage('Fetching authentication details...');
			const { message, college, profile, multipleInstituitions, colleges, role, icons, needStatData } = await checkAuth();

			if (message === 'Login Required') {
				setLoadingMessage('');
				if (authRoutes.includes(location.pathname)) {
					return history.push('/');
				} else if (location.pathname) {
					return history.push(location.pathname);
				} else {
					return history.push('/');
				}
			}

			if (message === 'Login Success' && profile?._id) {
				if (localStorage.getItem('collegeId') === '' || localStorage.getItem('collegeId') == 'undefined' || localStorage.getItem('collegeId') == '""') {
					await setCollegeId(college?._id);
					await oldSetUserId(college?._id);
				}
				if (collegeId === college?._id) {
					await setRole(role);
					await setIcons(icons);
				} else {
					const matchedCollege = colleges.find((c) => c._id === collegeId);
					await setRole(matchedCollege?.roles);
					await setIcons(matchedCollege?.icons);
				}

				await setSelectedCollege({ ...college, collegeId: college._id, ...colleges.find((c) => c._id === college?._id) });
				await setProfile(profile);
				await setCollegeList(colleges || [college]);
				await setUserId(profile?._id);
				storeAuthData({ college, profile, multipleInstituitions, colleges, role, icons, needStatData });

				setNeedData(needStatData || []);
				setMultipleInstituitions(multipleInstituitions ?? false);
				setAuthComplete(true);
				setLoadingMessage('');
			}
		} catch (error) {
			if (error.message === 'Network Error') {
				setLoadingMessage('Server Error. Please try again later.');
				if (!isTokenAvailable) {
					history.push(location.pathname || '/');
					setLoadingMessage('');
				}
			} else {
				if (error?.response?.data?.message === 'Your application has been suspended due to outstanding payments for usage. Please settle the balance to restore service.') {
					return setLoadingMessage('Your application has been suspended due to outstanding payments for usage. Please settle the balance to restore service.');
				}

				setLoadingMessage('Authentication failed. Please try again.');
			}
		}
	};

	useEffect(() => {
		const initializeAuth = async () => {
			await getAuthDetails();
		};
		initializeAuth();
	}, [collegeId, userId]);

	useEffect(() => {
		if (authComplete) {
			if (multipleInstituitions) {
				// if (location.pathname !== '/app') {
				return history.push(location.pathname);
				// }
				// history.replace('/college-list');
			} else {
				if (location.pathname !== '/college-list') {
					return history.push(location.pathname);
				}
				history.replace('/app');
			}
		}
	}, [authComplete, multipleInstituitions, location.pathname]);

	return loadingMessage !== '' ? (
		loadingMessage === 'Your application has been suspended due to outstanding payments for usage. Please settle the balance to restore service.' ? (
			<div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
				<div className='bg-white rounded-xl p-6 shadow-lg flex flex-col items-center gap-4 m-auto w-[50%] h-[40vh] justify-center'>
					<NecttosLogo height='60' />
					<span className='text-center text-red-900'>{loadingMessage}</span>
				</div>
			</div>
		) : (
			<div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
				<div className='bg-white rounded-xl p-6 shadow-lg flex flex-col items-center gap-4 w-[30%]'>
					<OLoading styles={{ height: 60, width: 60 }} />
					<p className='text-center text-gray-800 text-sm'>Loading...</p>
					<span>{loadingMessage}</span>
				</div>
			</div>
		)
	) : (
		children
	);
}

export default Auth;
